import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Dropdown, Field, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle, Option, Popover, PopoverSurface, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, tokens } from "@fluentui/react-components";
import { Delete16Regular, Dismiss16Regular, Send24Regular } from "@fluentui/react-icons";
import { addDays, format, startOfWeek } from "date-fns";
import type { Weekday } from "@gymplan/shared";
import { WEEKDAYS } from "@gymplan/shared";
import { api, type CheckIn, type Client } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { WeekCalendar, toHHMM, type CalBlock } from "../components/WeekCalendar.tsx";
import { ConfirmButton } from "../components/ConfirmButton.tsx";
import { MessageLog } from "../components/MessageLog.tsx";
import { TIME_OPTIONS } from "../lib/format.ts";

type Status = "PLANNING" | "HOLIDAY" | "COLLECTING" | "PROPOSED" | "PUBLISHED";
interface Session {
  id: string;
  startsAt: string;
  endsAt: string;
  status: "PROPOSED" | "CONFIRMED" | "CANCELLED" | "COMPLETED";
  client: { id: string; name: string };
  gym: { id: string; name: string };
}
interface WeekData {
  weekStart: string;
  timezone: string;
  schedule: { status: Status; deadlineAt: string | null; unplaced: { clientId: string; name: string; needed: number; placed: number; reason: string }[] | null; notes: { name: string; note: string }[] | null; trainerRemindedAt: string | null; holidayNotifiedAt: string | null; checkInsSentAt: string | null; builtAt: string | null; publishedAt: string | null };
  nextCheckInLabel: string;
  nextCheckInWeek: string;
  fallbackPublishLabel: string;
  availability: { source: "template" | "week"; slots: { id: string; weekday: Weekday; startTime: string; endTime: string; gymId: string | null }[] };
  sessions: Session[];
  checkIns: (CheckIn & { reminderSentAt: string | null; offeredAt: string | null })[];
}

const STATUS_LABEL: Record<Status, { text: string; color: "informative" | "warning" | "success" | "subtle" | "important" }> = {
  PLANNING: { text: "Planning", color: "subtle" },
  HOLIDAY: { text: "Holiday week", color: "important" },
  COLLECTING: { text: "Collecting replies", color: "informative" },
  PROPOSED: { text: "Ready to review", color: "warning" },
  PUBLISHED: { text: "Published", color: "success" },
};

const fmt = (iso: string) => format(new Date(iso), "EEE d MMM, HH:mm");
const WD_INDEX: Record<Weekday, number> = { MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6 };

/** The trainer's week hub: check-in status, the proposed/published timetable, and actions. */
export function SchedulePage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // No ?week → the API picks the week the next check-in plans for; we adopt it once loaded.
  const [week, setWeek] = useState<Date | null>(() => (params.get("week") ? new Date(`${params.get("week")}T00:00:00`) : null));
  const weekYmd = week ? format(week, "yyyy-MM-dd") : "";
  const changeWeek = (d: Date) => {
    setWeek(d);
    setParams({ week: format(d, "yyyy-MM-dd") });
    setSelected(null);
  };

  const q = useQuery({ queryKey: ["schedule", weekYmd], queryFn: () => api.get<WeekData>(weekYmd ? `/api/schedule?week=${weekYmd}` : "/api/schedule"), refetchInterval: 20_000 });
  useEffect(() => {
    if (!week && q.data) setWeek(new Date(`${q.data.weekStart}T00:00:00`));
  }, [week, q.data]);
  const clients = useQuery({ queryKey: ["clients"], queryFn: () => api.get<Client[]>("/api/clients") });
  const health = useQuery({ queryKey: ["health"], queryFn: () => api.get<{ whatsapp: boolean }>("/health") });
  const simulated = health.data && !health.data.whatsapp;
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["schedule", weekYmd] });
  const act = (path: string, body?: unknown) => useMutation({ mutationFn: () => api.post(path, { week: weekYmd, ...(body as object) }), onSuccess: invalidate });
  const holiday = useMutation({ mutationFn: (on: boolean) => api.post("/api/schedule/holiday", { week: weekYmd, holiday: on }), onSuccess: invalidate });
  const notifyHoliday = act("/api/schedule/holiday/notify");
  const sendCheckIns = act("/api/schedule/send-checkins");
  const build = act("/api/schedule/build");
  const publish = act("/api/schedule/publish");
  const moveSession = useMutation({ mutationFn: (v: { id: string; weekday: Weekday; startTime: string; endTime: string }) => api.patch(`/api/schedule/sessions/${v.id}`, { week: weekYmd, ...v }), onSuccess: invalidate });
  const removeSession = useMutation({ mutationFn: (id: string) => api.del(`/api/schedule/sessions/${id}`), onSuccess: invalidate });
  const addSession = useMutation({ mutationFn: (v: { clientId: string; gymId: string; weekday: Weekday; startTime: string; endTime: string }) => api.post("/api/schedule/sessions", { week: weekYmd, ...v }), onSuccess: invalidate });
  const simulate = useMutation({ mutationFn: (v: { clientId: string; text: string }) => api.post("/api/schedule/simulate-reply", v), onSuccess: invalidate });
  const askAlt = useMutation({ mutationFn: (clientId: string) => api.post<{ offered: number }>("/api/schedule/ask-alternatives", { week: weekYmd, clientId }), onSuccess: invalidate });

  const [selected, setSelected] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [newSlot, setNewSlot] = useState<{ weekday: Weekday; startTime: string; endTime: string; clientId: string } | null>(null);
  const [sim, setSim] = useState<Record<string, string>>({});

  const d = q.data;
  const status: Status = d?.schedule.status ?? "PLANNING";
  const gyms = user?.trainer?.gyms ?? [];
  const primary = gyms.find((g) => g.isPrimary);
  const editable = status !== "HOLIDAY";

  const blocks = useMemo<CalBlock[]>(() => {
    if (!d) return [];
    const avail: CalBlock[] = d.availability.slots.map((s) => ({ id: `avail-${s.id}`, weekday: s.weekday, startTime: s.startTime, endTime: s.endTime, kind: "availability", readOnly: true, title: gyms.length > 1 ? (gyms.find((g) => g.id === (s.gymId ?? primary?.id))?.name ?? "") : undefined }));
    const sess: CalBlock[] = d.sessions.map((s) => {
      const st = new Date(s.startsAt);
      const en = new Date(s.endsAt);
      const wd = WEEKDAYS[(st.getDay() + 6) % 7]!;
      return { id: s.id, weekday: wd, startTime: toHHMM(st.getHours() * 60 + st.getMinutes()), endTime: toHHMM(en.getHours() * 60 + en.getMinutes()), title: s.client.name, subtitle: gyms.length > 1 ? s.gym.name : undefined, kind: "session" };
    });
    return [...avail, ...sess];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, gyms]);

  const sel = selected ? d?.sessions.find((s) => s.id === selected.id) : undefined;
  const replied = d?.checkIns.filter((c) => c.status === "COMPLETED").length ?? 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <Text as="h1" size={800} weight="semibold" style={{ flex: 1 }}>
          Schedule
        </Text>
        {d && (
          <Badge appearance="filled" color={STATUS_LABEL[status].color} size="large">
            {STATUS_LABEL[status].text}
          </Badge>
        )}
      </div>
      {d && (
        <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: 12 }}>
          Next check-ins go out <b>{d.nextCheckInLabel}</b> for w/c {d.nextCheckInWeek}. {simulated && "WhatsApp is in simulation mode — messages are logged, not sent."}
        </Text>
      )}

      {/* Status-specific guidance + actions */}
      {d && status === "PLANNING" && (
        <MessageBar intent="info" layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Before check-ins go out</MessageBarTitle>
            Availability for this week is {d.availability.source === "week" ? "customised" : "your usual template"} — edit it on the Availability page (pick this week there). Away this week? Mark it as a holiday and every client is told.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Holiday week" message="No sessions will be scheduled and every client will be told at the usual check-in time (or sooner if you choose). Continue?" confirmLabel="Mark as holiday" onConfirm={() => holiday.mutate(true)}>Mark as holiday</ConfirmButton>
            <ConfirmButton size="small" appearance="primary" icon={<Send24Regular />} title="Send check-ins now?" message="Every active client gets the availability check-in on WhatsApp straight away, instead of at the scheduled time." confirmLabel="Send" onConfirm={() => sendCheckIns.mutate()}>Send check-ins now</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {d && status === "HOLIDAY" && (
        <MessageBar intent="warning" layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Holiday week</MessageBarTitle>
            {d.schedule.holidayNotifiedAt ? `Clients were told on ${fmt(d.schedule.holidayNotifiedAt)}.` : `Clients will be told at the usual check-in time. `}
          </MessageBarBody>
          {!d.schedule.holidayNotifiedAt && (
            <MessageBarActions>
              <Button size="small" onClick={() => holiday.mutate(false)}>Undo</Button>
              <ConfirmButton size="small" appearance="primary" title="Tell clients now?" message="Every active client gets a WhatsApp saying there are no sessions this week." confirmLabel="Send" onConfirm={() => notifyHoliday.mutate()}>Tell clients now</ConfirmButton>
            </MessageBarActions>
          )}
        </MessageBar>
      )}
      {d && status === "COLLECTING" && (
        <MessageBar intent="info" layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Waiting for replies — {replied} of {d.checkIns.length} back</MessageBarTitle>
            Deadline {d.schedule.deadlineAt ? fmt(d.schedule.deadlineAt) : "—"}. Non-responders get one reminder 6h before, then default to their usual times. The schedule builds itself when everyone's replied or the deadline passes.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Build now?" message="Builds the schedule from the replies so far. Anyone who hasn't replied defaults to their usual times." confirmLabel="Build" onConfirm={() => build.mutate()}>Build now</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {d && status === "PROPOSED" && (
        <MessageBar intent="warning" layout="multiline" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Review and publish</MessageBarTitle>
            Drag sessions to adjust, click one to remove, or drag on empty availability to add. Publishing confirms the timetable and WhatsApps every client their slots. If you do nothing it publishes itself <b>{d.fallbackPublishLabel}</b>.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Rebuild schedule?" message="Runs the placement again from scratch. Any manual changes to this week are lost." confirmLabel="Rebuild" onConfirm={() => build.mutate()}>Rebuild</ConfirmButton>
            <ConfirmButton size="small" appearance="primary" icon={<Send24Regular />} title="Publish this week?" message={`Confirms ${d.sessions.length} sessions and WhatsApps every client their slots.`} confirmLabel="Publish" onConfirm={() => publish.mutate()} disabled={publish.isPending}>Publish</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {d && status === "PUBLISHED" && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>Published {d.schedule.publishedAt ? fmt(d.schedule.publishedAt) : ""}. Changes you make now are saved but clients aren't re-notified automatically — message them directly.</MessageBarBody>
        </MessageBar>
      )}

      {/* Problems */}
      {d?.schedule.unplaced && d.schedule.unplaced.length > 0 && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Couldn't fit everyone</MessageBarTitle>
            {d.schedule.unplaced.map((u) => {
              const ci = d.checkIns.find((c) => c.client.id === u.clientId);
              const asked = ci?.offeredAt && ci.status !== "COMPLETED";
              return (
                <div key={u.clientId} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  <span>
                    <b>{u.name}</b>: {u.placed}/{u.needed} placed — {u.reason}
                  </span>
                  {asked ? (
                    <Badge appearance="tint" color="informative">Asked — waiting for reply</Badge>
                  ) : (
                    <Button size="small" onClick={() => askAlt.mutate(u.clientId)} disabled={askAlt.isPending}>
                      Ask {u.name.split(" ")[0]} to pick from free slots
                    </Button>
                  )}
                </div>
              );
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      {d?.schedule.notes && d.schedule.notes.length > 0 && (
        <MessageBar intent="warning" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Notes from clients</MessageBarTitle>
            {d.schedule.notes.map((n) => (
              <div key={n.name}>
                <b>{n.name}</b>: "{n.note}"
              </div>
            ))}
          </MessageBarBody>
        </MessageBar>
      )}

      <WeekCalendar
        blocks={blocks}
        weekStart={week ?? startOfWeek(new Date(), { weekStartsOn: 1 })}
        onWeekChange={changeWeek}
        editable={editable}
        defaultDurationMinutes={user?.trainer?.sessionLengthMinutes ?? 60}
        selectedId={selected?.id ?? null}
        onCreate={(b) => setNewSlot({ ...b, clientId: clients.data?.[0]?.id ?? "" })}
        onMove={(id, b) => moveSession.mutate({ id, ...b })}
        onSelect={(id, anchor) => {
          if (id.startsWith("avail-")) return;
          setTimeout(() => setSelected({ id, anchor }), 0);
        }}
        toolbar={
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {d ? `${d.sessions.length} sessions · availability: ${d.availability.source === "week" ? "this week only" : "template"}` : ""}
          </Text>
        }
      />

      {/* Session popover */}
      <Popover open={!!sel} positioning={{ target: selected?.anchor, position: "after", align: "top" }} onOpenChange={(_, data) => !data.open && setSelected(null)} trapFocus>
        <PopoverSurface style={{ width: 240, display: "flex", flexDirection: "column", gap: 8 }}>
          {sel && (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text weight="semibold" style={{ flex: 1 }}>{sel.client.name}</Text>
                <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label="Close" onClick={() => setSelected(null)} />
              </div>
              <Text size={200}>{fmt(sel.startsAt)} · {sel.gym.name} · {sel.status.toLowerCase()}</Text>
              <Button appearance="subtle" size="small" icon={<Delete16Regular />} onClick={() => { removeSession.mutate(sel.id); setSelected(null); }} style={{ alignSelf: "flex-start" }}>
                Remove session
              </Button>
            </>
          )}
        </PopoverSurface>
      </Popover>

      {/* Add-session dialog (after drag on empty space) */}
      <Popover open={!!newSlot} onOpenChange={(_, data) => !data.open && setNewSlot(null)} positioning="below" trapFocus>
        <PopoverSurface style={{ width: 300, display: "flex", flexDirection: "column", gap: 8 }}>
          {newSlot && (
            <>
              <Text weight="semibold">New session · {newSlot.weekday} {newSlot.startTime}–{newSlot.endTime}</Text>
              <Field label="Client" size="small">
                <Dropdown size="small" value={clients.data?.find((c) => c.id === newSlot.clientId)?.name ?? ""} selectedOptions={[newSlot.clientId]} onOptionSelect={(_, o) => setNewSlot({ ...newSlot, clientId: o.optionValue! })}>
                  {(clients.data ?? []).map((c) => (
                    <Option key={c.id} value={c.id} text={c.name}>{c.name}</Option>
                  ))}
                </Dropdown>
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Dropdown size="small" value={newSlot.startTime} selectedOptions={[newSlot.startTime]} onOptionSelect={(_, o) => setNewSlot({ ...newSlot, startTime: o.optionValue! })}>
                  {TIME_OPTIONS.map((x) => <Option key={x} value={x}>{x}</Option>)}
                </Dropdown>
                <Dropdown size="small" value={newSlot.endTime} selectedOptions={[newSlot.endTime]} onOptionSelect={(_, o) => setNewSlot({ ...newSlot, endTime: o.optionValue! })}>
                  {TIME_OPTIONS.map((x) => <Option key={x} value={x}>{x}</Option>)}
                </Dropdown>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button size="small" onClick={() => setNewSlot(null)}>Cancel</Button>
                <Button size="small" appearance="primary" disabled={!newSlot.clientId} onClick={() => { addSession.mutate({ ...newSlot, gymId: gymFor(d, newSlot.weekday, newSlot.startTime, primary?.id ?? "") }); setNewSlot(null); }}>Add</Button>
              </div>
            </>
          )}
        </PopoverSurface>
      </Popover>

      {/* Check-in table */}
      {d && d.checkIns.length > 0 && (
        <Card style={{ padding: 16, marginTop: 16 }}>
          <Text weight="semibold" size={400} block style={{ marginBottom: 8 }}>Check-ins</Text>
          <Table aria-label="Check-ins" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Outcome</TableHeaderCell>
                {simulated && <TableHeaderCell>Simulate reply</TableHeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.checkIns.map((ci) => (
                <TableRow key={ci.id}>
                  <TableCell>{ci.client.name}</TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={ci.status === "COMPLETED" ? "success" : ci.status === "EXPIRED" ? "subtle" : "informative"}>{ci.status.replace("_", " ")}</Badge>
                    {ci.reminderSentAt && <Text size={100} style={{ marginLeft: 6, color: tokens.colorNeutralForeground3 }}>reminded</Text>}
                    {ci.offeredAt && <Text size={100} style={{ marginLeft: 6, color: tokens.colorNeutralForeground3 }}>offered alternatives</Text>}
                  </TableCell>
                  <TableCell>
                    {ci.result ? (ci.result.keepPreferences ? "Usual times" : ci.result.notes ?? `${ci.result.overrides.length} change(s)`) : ci.status === "EXPIRED" ? "Defaulted to usual times" : "—"}{" "}
                    <MessageLog clientId={ci.client.id} clientName={ci.client.name} />
                  </TableCell>
                  {simulated && (
                    <TableCell>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(ci.status === "PENDING" || ci.status === "IN_PROGRESS") && (
                          <>
                            <input value={sim[ci.client.id] ?? ""} onChange={(e) => setSim({ ...sim, [ci.client.id]: e.target.value })} placeholder="yes / can't do Monday" style={{ width: 170 }} />
                            <Button size="small" onClick={() => simulate.mutate({ clientId: ci.client.id, text: sim[ci.client.id] || "yes" })}>Reply</Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {(holiday.error || build.error || publish.error || sendCheckIns.error) && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          <MessageBarBody>{String((holiday.error ?? build.error ?? publish.error ?? sendCheckIns.error as Error).message)}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}

/** Pick the gym of the availability block covering a slot (falls back to primary). */
function gymFor(d: WeekData | undefined, weekday: Weekday, startTime: string, primaryId: string) {
  const a = d?.availability.slots.find((s) => s.weekday === weekday && s.startTime <= startTime && startTime < s.endTime);
  return a?.gymId ?? primaryId;
}
void addDays;
void WD_INDEX;
